import Cookies from "js-cookie";

// STG環境判定
const isStagingEnv = (): boolean => {
  return window.location.hostname.includes("stg.ippon-cloud.com");
};

// STG環境の場合は "stg_" プレフィックスを付与（本番との競合防止）
const getCookieName = (name: string): string => {
  return isStagingEnv() ? `stg_${name}` : name;
};

// Cookie操作の共通設定
const getCookieOptions = () => {
  const hostname = window.location.hostname;
  const isStaging = hostname.includes("stg.ippon-cloud.com");
  const isProduction = hostname.includes("ippon-cloud.com") && !isStaging;
  const options: any = {
    path: "/",
  };

  if (isStaging) {
    options.domain = "stg.ippon-cloud.com";
    options.secure = true;
    options.sameSite = "Lax";
  } else if (isProduction) {
    options.domain = "ippon-cloud.com";
    options.secure = true;
    options.sameSite = "Lax";
  }

  return { isProduction: isProduction || isStaging, options };
};

// 共通のCookie取得関数
export const getSecureCookie = (name: string): string | null => {
  return Cookies.get(getCookieName(name)) || null;
};

// 共通のCookie設定関数
export const setSecureCookie = (
  name: string,
  value: string,
  expires?: number
): void => {
  const { options } = getCookieOptions();
  if (expires) {
    options.expires = expires;
  }
  Cookies.set(getCookieName(name), value, options);
};

// 共通のCookie削除関数
export const removeSecureCookie = (name: string): void => {
  const { options } = getCookieOptions();
  Cookies.remove(getCookieName(name), options);
};

interface TokenData {
  clientID: string;
  clientName: string;
  employeeID: string;
  role: string;
  email?: string;
  exp?: number;
  [key: string]: any;
}

/**
 * Base64 URLセーフエンコーディングをデコード（UTF-8対応）
 */
const base64UrlDecode = (str: string): string => {
  // パディングを追加
  str += "=".repeat((4 - (str.length % 4)) % 4);
  // URLセーフ文字を標準Base64文字に変換
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");

  // UTF-8デコードのためにTextDecoderを使用
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder("utf-8").decode(bytes);
  } catch (error) {
    // フォールバック: 従来の方法
    return atob(base64);
  }
};

/**
 * JWTトークンをデコードする
 */
const decodeJWT = (token: string): TokenData | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    const payload = parts[1];
    const decodedPayload = base64UrlDecode(payload);
    const parsedPayload = JSON.parse(decodedPayload);

    return parsedPayload;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * JWTトークンの有効期限をチェック
 * 注意: 現在は使用されていませんが、将来的に必要になる可能性があります
 */
// const isTokenExpired = (tokenData: TokenData): boolean => {
//   if (!tokenData.exp) return false;

//   const currentTime = Math.floor(Date.now() / 1000);
//   return tokenData.exp < currentTime;
// };

/**
 * トークンを取得してデコードされたデータを返す
 * 注意: 有効期限チェックは行わない（axios interceptorで処理されるため）
 */
const getTokenData = (): TokenData | null => {
  const token = getSecureCookie("token");
  if (!token) {
    return null;
  }

  const tokenData = decodeJWT(token);
  if (!tokenData) {
    return null;
  }

  // 有効期限チェックはaxios interceptorで事前に処理されるため、
  // ここでは期限切れトークンの削除は行わない
  return tokenData;
};

/**
 * クライアントIDを取得
 */
export const getClientID = (): string | null => {
  const tokenData = getTokenData();
  return (
    tokenData?.["custom:clientId"] ||
    tokenData?.clientID ||
    tokenData?.client_id ||
    null
  );
};

/**
 * 従業員IDを取得
 */
export const getEmployeeID = (): string | null => {
  const tokenData = getTokenData();
  return (
    tokenData?.sub || tokenData?.employeeID || tokenData?.employee_id || null
  );
};

/**
 * ロールを取得
 */
export const getRole = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.["custom:role"] || tokenData?.role || null;
};

/**
 * クライアント名を取得（JWTには含まれていないためAPIから取得が必要）
 */
export const getClientName = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.clientName || tokenData?.client_name || null;
};

/**
 * メールアドレスを取得
 */
export const getEmail = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.email || null;
};

/**
 * 期限切れチェックなしでトークンデータを取得（リフレッシュトークン用）
 */
const getTokenDataWithoutExpCheck = (): TokenData | null => {
  const token = getSecureCookie("token");
  if (!token) {
    return null;
  }

  const tokenData = decodeJWT(token);
  if (!tokenData) {
    return null;
  }

  // 期限切れチェックはしない
  return tokenData;
};

/**
 * Cognito usernameを取得（リフレッシュトークン用）
 * 期限切れのトークンからでもUsernameを取得する
 */
export const getCognitoUsername = (): string | null => {
  // 期限切れチェックなしでトークンデータを取得
  const tokenData = getTokenDataWithoutExpCheck();

  if (!tokenData) {
    return null;
  }

  // 複数のフィールドをチェック
  const username =
    tokenData?.["cognito:username"] ||
    tokenData?.username ||
    tokenData?.sub ||
    null;

  return username;
};

/**
 * 名前を取得
 */
export const getFirstName = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.given_name || null;
};

/**
 * 姓を取得
 */
export const getLastName = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.family_name || null;
};

/**
 * フルネームを取得
 */
export const getFullName = (): string | null => {
  const lastName = getLastName();
  const firstName = getFirstName();
  if (lastName && firstName) {
    return `${lastName} ${firstName}`;
  }
  return lastName || firstName || null;
};

/**
 * 認証状態をチェック
 */
export const isAuthenticated = (): boolean => {
  const tokenData = getTokenData();
  return tokenData !== null;
};

/**
 * すべてのトークンデータを取得（デバッグ用）
 */
export const getAllTokenData = (): TokenData | null => {
  return getTokenData();
};

/**
 * トークンをクリア
 */
export const clearToken = (): void => {
  removeSecureCookie("token");
  removeSecureCookie("refreshToken");
};

/**
 * リフレッシュトークンを取得
 */
export const getRefreshToken = (): string | null => {
  return getSecureCookie("refreshToken");
};

/**
 * サービスIDリストを取得
 */
export const getServiceIds = (): string[] => {
  const tokenData = getTokenData();
  console.log("tokenData", tokenData);
  const serviceIdsData = tokenData?.["custom:service_ids"];

  if (!serviceIdsData) {
    return [];
  }

  try {
    // 既に配列の場合はそのまま返す
    if (Array.isArray(serviceIdsData)) {
      return serviceIdsData.filter((id) => typeof id === "string" && id.trim());
    }

    // 文字列の場合はJSON.parseを試みる
    if (typeof serviceIdsData === "string") {
      console.log("Parsing service_ids string:", serviceIdsData);
      // JSON文字列をパース（例: '["id1","id2"]'）
      const parsed = JSON.parse(serviceIdsData);
      if (Array.isArray(parsed)) {
        return parsed.filter((id) => typeof id === "string" && id.trim());
      }
    }

    return [];
  } catch (error) {
    console.error("Error parsing service_ids:", error, "Value:", serviceIdsData);
    return [];
  }
};
